import type { CostCalculationProject } from "providers/HostedScrapingProvider/types";

type CostProjectParts = Pick<CostCalculationProject, "input" | "config"> &
  Partial<Pick<CostCalculationProject, "name" | "language">>;

/**
 * Builds a synthetic project payload for the `projectCost` estimate endpoint.
 *
 * Surfaces that don't yet have a real project (the crawler New/Edit form and the
 * API Playground) need a full CostCalculationProject just to ask "what would this
 * cost". Only `input`, `config` (and optionally `name`/`language`) affect the
 * estimate — the remaining fields are inert placeholders required by the endpoint
 * contract. Real projects (hosted scraping) pass their own object instead.
 */
export function buildCostProject(parts: CostProjectParts): CostCalculationProject {
  return {
    // Cost-relevant — these drive the estimate.
    input: parts.input,
    config: parts.config,
    name: parts.name ?? "",
    // Inert placeholders — required by the endpoint contract, ignored by the estimate.
    userId: 0,
    scrapingInterval: "daily",
    cron: "0 1 * * *",
    enabled: false,
    supposedToRunAt: undefined,
    output: { type: "save" },
    demoProject: false,
    notificationConfig: {
      notifyOnSuccess: "never",
      notifyOnFailure: "with_every_run",
      notificationChanged: new Date(),
    },
    schedulingIssue: undefined,
    ...(parts.language !== undefined ? { language: parts.language } : {}),
  };
}
